import React, { useEffect, useMemo, useRef, useState } from "react";

import { toast } from "react-toastify";
import {
	Bot,
	CheckCircle2,
	FlaskConical,
	Inbox,
	KeyRound,
	ListChecks,
	Loader2,
	PanelRight,
	Pencil,
	Plus,
	Send,
	ShieldAlert,
	Sparkles,
	TriangleAlert,
	Wrench,
	XCircle,
} from "lucide-react";

import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { cn } from "../../lib/utils";
import toastError from "../../errors/toastError";
import api from "../../services/api";

// O assistente montador: o admin descreve em português o agente que quer e o
// assistente monta tudo — instrução, ferramentas, credencial, cenários de
// teste — sem nunca ativar nada.
//
// A tela tem duas colunas: a conversa à esquerda e o painel do que já
// existe à direita. Os pedidos que dependem do admin (digitar a credencial,
// autorizar um teste de ação, ativar) aparecem nos dois lugares: no topo do
// painel, que é onde ele olha o que falta, e no fim da conversa, que é onde
// ele está lendo.
//
// Duas regras atravessam a tela inteira:
// 1. O segredo nunca é mostrado. O campo é de senha, o valor sai do estado
//    assim que o envio termina e ele nunca vira mensagem na conversa.
// 2. O assistente nunca ativa nada sozinho. O botão "Ativar" existe só
//    quando existe um pedido de ativação esperando.

const ROTULO_DO_TIPO_DE_CREDENCIAL = {
	bearer: "Token (Bearer)",
	basic: "Usuário e senha (Basic)",
	header: "Cabeçalho de autenticação",
	query: "Parâmetro na URL",
};

const ROTULO_DO_TIPO_DE_FERRAMENTA = {
	http: "API de fora",
	internal: "Ação interna",
};

const ROTULO_DO_MODO = {
	direto: "Direto",
	confirma_cliente: "Confirma com o cliente",
	aprovacao_atendente: "Aprovação do atendente",
};

// As ferramentas do assistente têm nome de máquina. Na tela elas viram frase:
// o admin lê "criou ferramenta", não "criar_ferramenta_api".
const VERBO_DA_FERRAMENTA = {
	listar_filas: "listou filas",
	listar_agentes: "listou agentes",
	listar_ferramentas: "listou ferramentas",
	listar_fluxos: "listou fluxos",
	listar_etiquetas: "listou etiquetas",
	listar_credenciais: "listou credenciais",
	criar_agente: "criou agente",
	editar_agente: "editou agente",
	ler_documentacao: "leu a documentação",
	criar_ferramenta_api: "criou ferramenta",
	editar_ferramenta_api: "editou ferramenta",
	pedir_credencial: "pediu credencial",
	testar_ferramenta: "testou ferramenta",
	criar_cenario: "criou cenário",
	apagar_cenario: "apagou cenário",
	rodar_cenarios: "rodou cenários",
	estudar_estilo: "estudou o jeito de falar da equipe",
	pedir_ativacao: "pediu ativação",
};

// A conversa do assistente vive no servidor por 24h, mas a tela só a guardava
// em memória: recarregar a página apagava tudo da vista. O id da sessão fica
// guardado no navegador para que a conversa possa ser retomada depois.
const CHAVE_DA_SESSAO = "npc:assistente:sessao";

// O proxy pode derrubar a conexão antes de o backend terminar o turno. Nesses
// casos a resposta ainda está sendo montada no servidor, então a tela espera
// pela sessão em vez de devolver um erro que não é o resultado do assistente.
const INTERVALO_DO_POLLING = 10 * 1000;
const LIMITE_DO_POLLING = 10 * 60 * 1000;

const erroTemporarioDoAssistente = (err) => {
	const status = err?.response?.status;

	if ([408, 502, 503, 504].includes(status)) return true;

	// Sem resposta HTTP é erro de rede ou timeout: o servidor pode ter
	// continuado o trabalho mesmo depois de a conexão cair.
	return !status && Boolean(err);
};

// O localStorage pode estar bloqueado (modo privado, política da empresa) e
// qualquer acesso a ele lança. A tela precisa funcionar sem ele: a conversa
// só não sobrevive ao F5.
const lerSessaoGuardada = () => {
	try {
		return window.localStorage.getItem(CHAVE_DA_SESSAO);
	} catch {
		return null;
	}
};

// Guardar ou apagar o id guardado. A chave sai junto com a conversa: deixar
// um id morto guardado faz o próximo F5 procurar uma sessão que não existe.
const guardarSessao = (id) => {
	try {
		if (id) {
			window.localStorage.setItem(CHAVE_DA_SESSAO, id);
		} else {
			window.localStorage.removeItem(CHAVE_DA_SESSAO);
		}
	} catch {
		// Sem storage a conversa continua funcionando, só não é retomada.
	}
};

const horaDaMensagem = (at) => {
	if (!at) return "";

	const data = new Date(at);

	if (Number.isNaN(data.getTime())) return "";

	return data.toLocaleTimeString("pt-BR", {
		hour: "2-digit",
		minute: "2-digit",
	});
};

const textoDoValor = (valor) =>
	typeof valor === "string" ? valor : JSON.stringify(valor);

// O nome da ferramenta criada costuma estar no resumo da ação. Quando está,
// o chip mostra "criou ferramenta consultar_cep" em vez de só "criou
// ferramenta".
const nomeNoResumo = (resumo) => {
	if (!resumo) return null;

	try {
		const dados = JSON.parse(resumo);

		if (dados && typeof dados.name === "string") return dados.name;
	} catch {
		// O resumo não é JSON: o chip fica só com o verbo.
	}

	return null;
};

const tituloDoPedido = (pedido, nomeDaFerramenta) => {
	if (pedido.tipo === "credencial") {
		return `Credencial: ${pedido.dados?.name || "sem nome"}`;
	}

	if (pedido.tipo === "teste_acao") {
		return `Teste de ferramenta: ${nomeDaFerramenta}`;
	}

	return "Ativar o agente";
};

// O cartão do pedido. Ele aparece no topo do painel e no fim da conversa, e
// o `prefixo` deixa os campos com id próprio em cada lugar.
const CartaoDoPedido = ({
	pedido,
	prefixo,
	segredo,
	aoMudarSegredo,
	aoResponder,
	occupado,
	nomeDaFerramenta,
	nomeDaFila,
	nomeDoAgente,
}) => {
	const dados = pedido.dados || {};
	const pendente = pedido.status === "pendente";

	return (
		<div className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-3">
			<div className="flex flex-wrap items-start justify-between gap-2">
				<p className="text-sm font-medium">
					{tituloDoPedido(pedido, nomeDaFerramenta)}
				</p>

				{pendente ? (
					<span className="shrink-0 rounded-full bg-amber-500/10 px-2 py-0.5 text-[10px] font-medium text-amber-600 dark:text-amber-400">
						esperando você
					</span>
				) : (
					<span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
						{pedido.status === "recusado" ? "recusado" : "concluído"}
					</span>
				)}
			</div>

			{/* ---------------- credencial ---------------- */}
			{pedido.tipo === "credencial" && (
				<>
					<div className="mt-1 space-y-0.5 text-xs text-muted-foreground">
						<p>
							Tipo:{" "}
							<span className="font-medium text-foreground">
								{ROTULO_DO_TIPO_DE_CREDENCIAL[dados.type] || dados.type}
							</span>
							{dados.headerName ? ` · no cabeçalho ${dados.headerName}` : ""}
						</p>
						{dados.motivo && <p className="leading-relaxed">{dados.motivo}</p>}
					</div>

					{pendente && (
						<>
							<div className="mt-2 space-y-1.5">
								<Label htmlFor={`${prefixo}-segredo-${pedido.id}`}>
									Cole aqui o token/senha
								</Label>
								<Input
									id={`${prefixo}-segredo-${pedido.id}`}
									type="password"
									autoComplete="off"
									value={segredo || ""}
									onChange={(e) => aoMudarSegredo(e.target.value)}
									placeholder="Cole aqui o token/senha"
									disabled={occupado}
								/>
								<p className="text-[11px] leading-relaxed text-muted-foreground">
									O valor vai direto para o cofre de credenciais e não aparece
									em mais nenhum lugar.
								</p>
							</div>

							<div className="mt-2.5 flex flex-wrap gap-2">
								<Button
									size="sm"
									className="h-8 text-xs"
									disabled={occupado || !(segredo || "").trim()}
									onClick={() => aoResponder(pedido, "concluir")}
								>
									{occupado && (
										<Loader2 className="h-3.5 w-3.5 animate-spin" />
									)}
									Salvar com segurança
								</Button>
								<Button
									size="sm"
									variant="outline"
									className="h-8 text-xs"
									disabled={occupado}
									onClick={() => aoResponder(pedido, "recusar")}
								>
									Recusar
								</Button>
							</div>
						</>
					)}
				</>
			)}

			{/* ---------------- teste de ação ---------------- */}
			{pedido.tipo === "teste_acao" && (
				<>
					<div className="mt-1 space-y-0.5 text-xs text-muted-foreground">
						<p>
							Ferramenta:{" "}
							<span className="font-medium text-foreground">
								{nomeDaFerramenta}
							</span>
						</p>

						{Object.keys(dados.params || {}).length > 0 && (
							<ul className="space-y-0.5">
								{Object.entries(dados.params).map(([chave, valor]) => (
									<li key={chave}>
										<span className="font-medium text-foreground">
											{chave}:
										</span>{" "}
										{textoDoValor(valor)}
									</li>
								))}
							</ul>
						)}
					</div>

					<p className="mt-1.5 flex items-center gap-1.5 text-xs font-medium text-destructive">
						<TriangleAlert className="h-3.5 w-3.5 shrink-0" />
						Isto vai executar a ação de verdade no sistema
					</p>

					{pendente && (
						<div className="mt-2.5 flex flex-wrap gap-2">
							<Button
								size="sm"
								className="h-8 text-xs"
								disabled={occupado}
								onClick={() => aoResponder(pedido, "concluir")}
							>
								{occupado && (
									<Loader2 className="h-3.5 w-3.5 animate-spin" />
								)}
								Permitir este teste
							</Button>
							<Button
								size="sm"
								variant="outline"
								className="h-8 text-xs"
								disabled={occupado}
								onClick={() => aoResponder(pedido, "recusar")}
							>
								Não permitir
							</Button>
						</div>
					)}
				</>
			)}

			{/* ---------------- ativação ---------------- */}
			{pedido.tipo === "ativacao" && (
				<>
					<div className="mt-1 space-y-0.5 text-xs text-muted-foreground">
						{dados.resumo && (
							<p className="leading-relaxed">{dados.resumo}</p>
						)}
						<p>
							Agente:{" "}
							<span className="font-medium text-foreground">
								{nomeDoAgente || `agente #${dados.agentId}`}
							</span>
						</p>
						<p>
							Fila:{" "}
							<span className="font-medium text-foreground">
								{nomeDaFila || "nenhuma"}
							</span>
						</p>
					</div>

					{pendente && (
						<div className="mt-2.5 flex flex-wrap gap-2">
							<Button
								size="sm"
								className="h-8 text-xs"
								disabled={occupado}
								onClick={() => aoResponder(pedido, "concluir")}
							>
								{occupado && (
									<Loader2 className="h-3.5 w-3.5 animate-spin" />
								)}
								Ativar
							</Button>
							<Button
								size="sm"
								variant="outline"
								className="h-8 text-xs"
								disabled={occupado}
								onClick={() => aoResponder(pedido, "recusar")}
							>
								Agora não
							</Button>
						</div>
					)}
				</>
			)}
		</div>
	);
};

// Uma fala da conversa: o admin à direita, o assistente à esquerda e o aviso
// do sistema no meio. Sob a resposta do assistente ficam as fichas do que
// ele fez.
const MensagemDoConversa = ({ mensagem }) => {
	const { role, texto, at, acoes } = mensagem;

	if (role === "sistema") {
		return (
			<div className="flex justify-center py-1">
				<p className="max-w-md text-center text-[11px] leading-relaxed text-muted-foreground">
					{texto}
				</p>
			</div>
		);
	}

	const doAdmin = role === "admin";

	return (
		<div
			className={cn(
				"flex flex-col gap-1",
				doAdmin ? "items-end" : "items-start"
			)}
		>
			<div
				className={cn(
					"max-w-[85%] whitespace-pre-wrap rounded-xl px-3 py-2 text-sm leading-relaxed",
					doAdmin ? "bg-primary text-primary-foreground" : "bg-muted"
				)}
			>
				{texto}
			</div>

			<div className="flex items-center gap-2 text-[10px] text-muted-foreground">
				<span>{horaDaMensagem(at)}</span>
			</div>

			{!doAdmin && acoes?.length > 0 && (
				<div className="flex max-w-[85%] flex-wrap gap-1">
					{acoes.map((acao, indice) => {
						const verbo =
							VERBO_DA_FERRAMENTA[acao.ferramenta] || acao.ferramenta;
						const nome = nomeNoResumo(acao.resumo);

						return (
							<span
								key={`${acao.ferramenta}-${indice}`}
								title={acao.ferramenta}
								className={cn(
									"inline-flex max-w-full items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium",
									acao.ok
										? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
										: "bg-destructive/10 text-destructive"
								)}
							>
								{acao.ok ? (
									<CheckCircle2 className="h-3 w-3 shrink-0" />
								) : (
									<XCircle className="h-3 w-3 shrink-0" />
								)}
								<span className="truncate">
									{verbo}
									{nome ? ` ${nome}` : ""}
									{!acao.ok && acao.resumo ? `: ${acao.resumo}` : ""}
								</span>
							</span>
						);
					})}
				</div>
			)}
		</div>
	);
};

const AbaAssistente = ({ dados, aoMudar, onIrPara }) => {
	const [texto, setTexto] = useState("");
	const [enviando, setEnviando] = useState(false);
	const [aguardandoResposta, setAguardandoResposta] = useState(false);
	const [criando, setCriando] = useState(false);
	const [decidindo, setDecidindo] = useState(null);
	// O segredo digitado no campo seguro. Ele fica em estado só enquanto o
	// admin digita e some assim que o envio termina.
	const [segredos, setSegredos] = useState({});
	const [avisoModulo, setAvisoModulo] = useState(null);
	const [painelAberto, setPainelAberto] = useState(false);

	const fimDaConversa = useRef(null);
	const iniciado = useRef(false);
	const cancelarPolling = useRef(null);

	const sessao = dados?.sessao || null;
	const painel = dados?.painel || null;

	const mensagens = useMemo(() => sessao?.mensagens || [], [sessao]);

	const pendentes = useMemo(
		() => (sessao?.pedidos || []).filter((p) => p.status === "pendente"),
		[sessao]
	);

	const concluidos = useMemo(
		() => (sessao?.pedidos || []).filter((p) => p.status !== "pendente"),
		[sessao]
	);

	useEffect(() => {
		const alvo = fimDaConversa.current;
		if (alvo) alvo.scrollIntoView({ block: "end" });
	}, [mensagens.length, pendentes.length, enviando, aguardandoResposta]);

	useEffect(() => {
		return () => {
			if (cancelarPolling.current) {
				cancelarPolling.current();
				cancelarPolling.current = null;
			}
		};
	}, []);

	const recarregar = async () => {
		if (!sessao?.id) return;

		try {
			const { data } = await api.get(`/ai/assistant/sessions/${sessao.id}`);
			aoMudar({ sessao: data.sessao, painel: data.painel });
		} catch {
			// A conversa não existe mais no servidor: a chave guardada sai
			// junto, senão o próximo F5 insiste na mesma sessão.
			guardarSessao(null);
			aoMudar(null);
		}
	};

	const tratarErro = async (err) => {
		const status = err?.response?.status;
		const mensagem =
			err?.response?.data?.error || err?.response?.data?.message || "";

		// Empresa sem o módulo de agentes de IA não chega nem na primeira
		// mensagem: a tela avisa em vez de tomar erro seco.
		if (status === 403 && mensagem.includes("ERR_MODULE_NOT_CONTRACTED")) {
			setAvisoModulo("Módulo Agentes de IA não contratado");
			return;
		}

		// O assistente monta o agente com ferramentas: sem um provedor que
		// aceite ferramentas, ele não funciona.
		if (mensagem.includes("ERR_AI_NO_TOOL_PROVIDER")) {
			toast.error(
				"O assistente precisa de um provedor OpenAI-compatível ou Anthropic ativo (aba Provedores)."
			);
			return;
		}

		if (status === 410) {
			toast.error("A conversa venceu, comece outra.");
			// A sessão venceu: nada mais vai conseguir falar com ela, então a
			// chave guardada sai antes de a tela esvaziar.
			guardarSessao(null);
			aoMudar(null);
			return;
		}

		toastError(err);

		// A ação pode ter acontecido antes da falha: recarregar mostra o
		// estado certo, com o aviso que o backend gravou na conversa.
		if (status >= 500) {
			await recarregar();
		}
	};

	const aguardarResposta = (id, mensagensAntes, pedidoId = null) => {
		if (!id) return;

		if (cancelarPolling.current) {
			cancelarPolling.current();
		}

		let cancelado = false;
		let timerConsulta = null;
		let timerLimite = null;

		const parar = () => {
			cancelado = true;

			if (timerConsulta) clearTimeout(timerConsulta);
			if (timerLimite) clearTimeout(timerLimite);
		};

		const finalizar = () => {
			if (cancelarPolling.current === parar) {
				cancelarPolling.current = null;
			}

			setEnviando(false);
			setAguardandoResposta(false);

			if (pedidoId) {
				setDecidindo((atual) => (atual === pedidoId ? null : atual));
			}
		};

		const encerrar = (erro) => {
			if (cancelado) return;

			parar();
			finalizar();

			if (erro) toast.error(erro);
		};

		const consultar = async () => {
			if (cancelado) return;

			try {
				const { data } = await api.get(`/ai/assistant/sessions/${id}`);

				if (cancelado) return;

				const total = Array.isArray(data?.sessao?.mensagens)
					? data.sessao.mensagens.length
					: 0;

				if (total > mensagensAntes) {
					encerrar();
					aoMudar({ sessao: data.sessao, painel: data.painel });
					return;
				}
			} catch (err) {
				if (cancelado) return;

				const status = err?.response?.status;

				if ([401, 403, 404, 410].includes(status)) {
					encerrar();
					await tratarErro(err);
					return;
				}
			}

			if (cancelado) return;

			timerConsulta = setTimeout(consultar, INTERVALO_DO_POLLING);
		};

		cancelarPolling.current = parar;
		setAguardandoResposta(true);
		timerLimite = setTimeout(() => {
			encerrar(
				"O assistente ainda está trabalhando, mas não respondeu em 10 minutos."
			);
		}, LIMITE_DO_POLLING);
		timerConsulta = setTimeout(consultar, INTERVALO_DO_POLLING);
	};

	const criarSessao = async () => {
		const { data } = await api.post("/ai/assistant/sessions", {});

		aoMudar({ sessao: data.sessao, painel: data.painel });

		return data.sessao;
	};

	// O id da conversa que está na tela. Ele protege a conversa guardada que
	// volta do servidor de não sobrescrever uma conversa que a pessoa já
	// começou enquanto ela não tinha chegado.
	const sessaoNaTela = useRef(null);

	// O id da conversa fica guardado no navegador a cada troca de sessão: é
	// o que permite retomar a conversa depois de recarregar a página.
	useEffect(() => {
		if (!sessao?.id) return;

		sessaoNaTela.current = sessao.id;
		guardarSessao(sessao.id);
	}, [sessao?.id]);

	// A conversa começa sozinha quando a aba abre: é mais simples do que
	// pedir um clique antes de poder falar. Se já existe uma conversa guardada
	// no navegador, ela é retomada em vez de recomeçar do zero.
	useEffect(() => {
		if (iniciado.current) return;

		iniciado.current = true;

		if (dados) return;

		const guardada = lerSessaoGuardada();

		if (!guardada) {
			criarSessao().catch(tratarErro);
			return;
		}

		api
			.get(`/ai/assistant/sessions/${guardada}`)
			.then(({ data }) => {
				// A pessoa já começou outra conversa enquanto esta voltava:
				// a que está na tela é a que vale.
				if (sessaoNaTela.current) return;

				aoMudar({ sessao: data.sessao, painel: data.painel });
			})
			.catch(() => {
				// A conversa guardada já venceu (24h) ou foi apagada: a chave
				// fica sem dono e a conversa recomeça.
				guardarSessao(null);
				criarSessao().catch(tratarErro);
			});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const novaConversa = async () => {
		if (cancelarPolling.current) {
			cancelarPolling.current();
			cancelarPolling.current = null;
			setEnviando(false);
			setAguardandoResposta(false);
			setDecidindo(null);
		}

		setCriando(true);

		try {
			if (sessao?.id) {
				try {
					await api.delete(`/ai/assistant/sessions/${sessao.id}`);
				} catch {
					// Sessão vencida ou já apagada: o que importa é a nova.
				}
			}

			// A conversa antiga morre agora: a chave guardada sai junto, para
			// que um F5 no meio do caminho não tente retomá-la.
			guardarSessao(null);

			setSegredos({});
			await criarSessao();
			toast.success("Conversa nova iniciada.");
		} catch (err) {
			await tratarErro(err);
		}

		setCriando(false);
	};

	const enviar = async () => {
		const fala = texto.trim();

		if (!fala || enviando || aguardandoResposta) return;

		setEnviando(true);

		let id = sessao?.id;
		let mensagensAntes = sessao?.mensagens?.length || 0;
		let iniciouEspera = false;

		try {
			// A primeira fala cria a conversa: ninguém precisa clicar em
			// "Nova conversa" para começar.
			if (!id) {
				const nova = await criarSessao();
				id = nova?.id;
				mensagensAntes = nova?.mensagens?.length || 0;
			}

			if (!id) {
				toast.error("Não consegui iniciar a conversa.");
				setEnviando(false);
				return;
			}

			const { data } = await api.post(
				`/ai/assistant/sessions/${id}/messages`,
				{ text: fala }
			);

			aoMudar({ sessao: data.sessao, painel: data.painel });
			setTexto("");
		} catch (err) {
			if (id && erroTemporarioDoAssistente(err)) {
				// A mensagem pode já estar sendo processada no servidor. Limpa
				// a caixa para ela não voltar quando o polling trouxer a
				// resposta, e espera a conversa ganhar uma mensagem nova.
				setTexto("");
				aguardarResposta(id, mensagensAntes);
				iniciouEspera = true;
			} else {
				await tratarErro(err);
			}
		}

		if (!iniciouEspera) setEnviando(false);
	};

	const responderPedido = async (pedido, acao) => {
		if (!sessao?.id) return;

		const id = sessao.id;
		const mensagensAntes = sessao.mensagens?.length || 0;
		let iniciouEspera = false;

		setDecidindo(pedido.id);

		try {
			const corpo = { acao };

			// O segredo vai direto para o cofre de credenciais: ele não é
			// mostrado, não vira mensagem e sai do estado assim que o envio
			// termina.
			if (acao === "concluir" && pedido.tipo === "credencial") {
				corpo.secret = segredos[pedido.id] || "";
			}

			const { data } = await api.post(
				`/ai/assistant/sessions/${id}/requests/${pedido.id}`,
				corpo
			);

			aoMudar({ sessao: data.sessao, painel: data.painel });

			toast.success(
				acao === "concluir" ? "Pedido concluído." : "Pedido recusado."
			);
		} catch (err) {
			if (erroTemporarioDoAssistente(err)) {
				// O painel já pode ter enviado a resposta ao backend. A tela
				// fica esperando a nova mensagem da sessão antes de liberar
				// os pedidos de novo.
				aguardarResposta(id, mensagensAntes, pedido.id);
				iniciouEspera = true;
			} else {
				await tratarErro(err);
			}
		} finally {
			// O segredo não fica guardado: ele sai do estado assim que o
			// envio termina, exista erro ou não.
			setSegredos((atual) => {
				const copia = { ...atual };
				delete copia[pedido.id];
				return copia;
			});

			if (!iniciouEspera) setDecidindo(null);
		}
	};

	const aoTeclar = (evento) => {
		if (evento.key !== "Enter" || evento.shiftKey) return;

		evento.preventDefault();
		enviar();
	};

	const aoMudarSegredo = (pedidoId, valor) => {
		setSegredos((atual) => ({ ...atual, [pedidoId]: valor }));
	};

	const nomeDaFerramentaDoPedido = (pedido) => {
		const id = Number(pedido.dados?.toolId);
		const ferramenta = (painel?.ferramentas || []).find(
			(item) => item.id === id
		);

		return ferramenta?.name || `ferramenta #${id}`;
	};

	const nomeDaFilaDoPedido = (pedido) => {
		const id = Number(pedido.dados?.queueId);
		const fila = (painel?.filas || []).find((item) => item.id === id);

		return fila?.name || "";
	};

	const cartaoDoPedido = (pedido, prefixo) => (
		<CartaoDoPedido
			key={`${prefixo}-${pedido.id}`}
			pedido={pedido}
			prefixo={prefixo}
			segredo={segredos[pedido.id]}
			aoMudarSegredo={(valor) => aoMudarSegredo(pedido.id, valor)}
			aoResponder={responderPedido}
			occupado={decidindo === pedido.id || aguardandoResposta}
			nomeDaFerramenta={nomeDaFerramentaDoPedido(pedido)}
			nomeDaFila={nomeDaFilaDoPedido(pedido)}
			nomeDoAgente={painel?.agente?.name}
		/>
	);

	if (avisoModulo) {
		return (
			<div className="rounded-xl border bg-card py-16 text-center">
				<ShieldAlert className="mx-auto h-8 w-8 text-muted-foreground/50" />
				<p className="mt-3 text-sm font-medium">{avisoModulo}</p>
				<p className="mx-auto mt-1 max-w-sm text-xs leading-relaxed text-muted-foreground">
					O assistente faz parte do módulo de agentes de IA. Fale com quem
					administra a sua conta para liberar o módulo.
				</p>
			</div>
		);
	}

	return (
		<div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_340px]">
			{/* ---------------- conversa ---------------- */}
			<section className="flex min-w-0 flex-col rounded-xl border bg-card">
				<header className="flex flex-wrap items-center gap-2 border-b px-4 py-3">
					<Sparkles className="h-4 w-4 text-muted-foreground" />
					<p className="text-sm font-medium">Assistente de agentes</p>

					{sessao?.agentId ? (
						<span className="text-xs text-muted-foreground">
							agente #{sessao.agentId} em construção
						</span>
					) : null}

					<div className="ml-auto flex items-center gap-2">
						<Button
							variant="ghost"
							size="sm"
							className="h-8 text-xs lg:hidden"
							onClick={() => setPainelAberto((atual) => !atual)}
						>
							<PanelRight className="h-3.5 w-3.5" />
							Painel
						</Button>

						<Button
							variant="outline"
							size="sm"
							className="h-8 text-xs"
							disabled={criando}
							onClick={novaConversa}
						>
							{criando ? (
								<Loader2 className="h-3.5 w-3.5 animate-spin" />
							) : (
								<Plus className="h-3.5 w-3.5" />
							)}
							Nova conversa
						</Button>
					</div>
				</header>

				<div className="max-h-[60vh] min-h-[240px] space-y-3 overflow-y-auto p-4">
					{mensagens.length === 0 && !enviando && !aguardandoResposta && (
						<p className="py-10 text-center text-xs leading-relaxed text-muted-foreground">
							Descreva em português o agente que você quer: em qual fila ele
							atende, como deve conversar e o que ele precisa consultar.
						</p>
					)}

					{mensagens.map((mensagem, indice) => (
						<MensagemDoConversa
							key={`${mensagem.at}-${indice}`}
							mensagem={mensagem}
						/>
					))}

					{/* Os pedidos pendentes também aparecem no fim da conversa:
					    é onde a pessoa está lendo quando o assistente pede algo. */}
					{pendentes.map((pedido) => cartaoDoPedido(pedido, "chat"))}

					{(enviando || aguardandoResposta) && (
						<div className="flex items-center gap-2 text-xs text-muted-foreground">
							<Loader2 className="h-3.5 w-3.5 animate-spin" />
							{aguardandoResposta
								? "O assistente ainda está trabalhando…"
								: "assistente pensando…"}
						</div>
					)}

					<div ref={fimDaConversa} />
				</div>

				<div className="border-t p-3">
					<div className="flex items-end gap-2">
						<textarea
							rows={2}
							value={texto}
							disabled={enviando || aguardandoResposta}
							onChange={(e) => setTexto(e.target.value)}
							onKeyDown={aoTeclar}
							placeholder="Descreva o agente que você quer montar..."
							aria-label="Fala para o assistente"
							className="flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
						/>
						<Button
							className="h-10 shrink-0"
							disabled={enviando || aguardandoResposta || !texto.trim()}
							onClick={enviar}
						>
							{enviando || aguardandoResposta ? (
								<Loader2 className="h-4 w-4 animate-spin" />
							) : (
								<Send className="h-4 w-4" />
							)}
							Enviar
						</Button>
					</div>

					<p className="mt-1.5 text-[11px] text-muted-foreground">
						Enter envia · Shift+Enter quebra a linha. Nada é ativado sem a sua
						decisão.
					</p>
				</div>
			</section>

			{/* ---------------- painel ---------------- */}
			<aside
				className={cn(
					"min-w-0 space-y-3",
					painelAberto ? "block" : "hidden lg:block"
				)}
			>
				{/* Os pedidos pendentes ficam no topo do painel: é o que falta
				    para a conversa andar. */}
				{pendentes.length > 0 && (
					<section className="space-y-2">
						<header className="flex items-center gap-2">
							<p className="text-sm font-medium">Esperando você</p>
							<span className="rounded-full bg-amber-500/10 px-2 py-0.5 text-[10px] font-medium text-amber-600 dark:text-amber-400">
								{pendentes.length}
							</span>
						</header>

						{pendentes.map((pedido) => cartaoDoPedido(pedido, "painel"))}
					</section>
				)}

				{/* ---------------- agente em construção ---------------- */}
				<section className="rounded-xl border bg-card p-3">
					<header className="flex items-center justify-between gap-2">
						<p className="text-sm font-medium">Agente em construção</p>
						<Bot className="h-4 w-4 text-muted-foreground" />
					</header>

					{painel?.agente ? (
						<>
							<div className="mt-2 flex items-center justify-between gap-2">
								<p className="min-w-0 truncate text-sm font-medium">
									{painel.agente.name}
								</p>
								<span
									className={cn(
										"shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium",
										painel.agente.status === "active"
											? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
											: "bg-amber-500/10 text-amber-600 dark:text-amber-400"
									)}
								>
									{painel.agente.status === "active" ? "Ativo" : "Rascunho"}
								</span>
							</div>

							{painel.agente.prompt && (
								<pre className="mt-2 max-h-40 overflow-auto whitespace-pre-wrap break-words rounded-md bg-muted p-2 text-[11px] leading-relaxed">
									{painel.agente.prompt}
								</pre>
							)}

							<Button
								variant="outline"
								size="sm"
								className="mt-2 h-7 text-xs"
								onClick={() => onIrPara("agentes")}
							>
								<Pencil className="h-3.5 w-3.5" />
								Editar na aba Agentes
							</Button>
						</>
					) : (
						<p className="mt-2 text-xs leading-relaxed text-muted-foreground">
							O assistente ainda não criou o agente. Conte o que ele deve
							fazer.
						</p>
					)}
				</section>

				{/* ---------------- ferramentas ---------------- */}
				<section className="rounded-xl border bg-card p-3">
					<header className="flex items-center justify-between gap-2">
						<p className="text-sm font-medium">Ferramentas</p>
						<Wrench className="h-4 w-4 text-muted-foreground" />
					</header>

					{painel?.ferramentas?.length ? (
						<ul className="mt-2 space-y-1.5">
							{painel.ferramentas.map((ferramenta) => (
								<li
									key={ferramenta.id}
									className="flex items-start justify-between gap-2 text-xs"
								>
									<div className="min-w-0">
										<p className="truncate font-medium">
											{ferramenta.name}
										</p>
										<p className="text-muted-foreground">
											{ROTULO_DO_TIPO_DE_FERRAMENTA[ferramenta.kind] ||
												ferramenta.kind}{" "}
											·{" "}
											{ROTULO_DO_MODO[ferramenta.executionMode] ||
												ferramenta.executionMode}
										</p>
									</div>

									<span
										className={cn(
											"shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium",
											ferramenta.status === "active"
												? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
												: "bg-amber-500/10 text-amber-600 dark:text-amber-400"
										)}
									>
										{ferramenta.status === "active" ? "Ativa" : "Rascunho"}
									</span>
								</li>
							))}
						</ul>
					) : (
						<p className="mt-2 text-xs text-muted-foreground">
							Nenhuma ferramenta ainda.
						</p>
					)}
				</section>

				{/* ---------------- cenários ---------------- */}
				<section className="rounded-xl border bg-card p-3">
					<header className="flex items-center justify-between gap-2">
						<p className="text-sm font-medium">Cenários de teste</p>
						<ListChecks className="h-4 w-4 text-muted-foreground" />
					</header>

					{painel?.cenarios?.length ? (
						<ul className="mt-2 space-y-1.5">
							{painel.cenarios.map((cenario) => (
								<li
									key={cenario.id}
									className="flex items-center gap-2 text-xs"
								>
									{cenario.lastResult ? (
										cenario.lastResult.passou ? (
											<CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-emerald-500" />
										) : (
											<XCircle className="h-3.5 w-3.5 shrink-0 text-destructive" />
										)
									) : (
										<span className="h-3.5 w-3.5 shrink-0 rounded-full border border-dashed border-muted-foreground/50" />
									)}

									<span className="min-w-0 flex-1 truncate">
										{cenario.name}
									</span>
								</li>
							))}
						</ul>
					) : (
						<p className="mt-2 text-xs text-muted-foreground">
							Nenhum cenário ainda.
						</p>
					)}

					<Button
						variant="outline"
						size="sm"
						className="mt-2 h-7 text-xs"
						onClick={() => onIrPara("simulador")}
					>
						<FlaskConical className="h-3.5 w-3.5" />
						Abrir no Simulador
					</Button>
				</section>

				{/* ---------------- filas ---------------- */}
				<section className="rounded-xl border bg-card p-3">
					<header className="flex items-center justify-between gap-2">
						<p className="text-sm font-medium">Filas</p>
						<Inbox className="h-4 w-4 text-muted-foreground" />
					</header>

					{painel?.filas?.length ? (
						<ul className="mt-2 space-y-1">
							{painel.filas.map((fila) => {
								const comAgente =
									painel.agente && fila.aiAgentId === painel.agente.id;

								return (
									<li
										key={fila.id}
										className="flex items-center justify-between gap-2 text-xs"
									>
										<span className="min-w-0 truncate">{fila.name}</span>

										{comAgente && (
											<span className="shrink-0 rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-medium text-primary">
												com este agente
											</span>
										)}
									</li>
								);
							})}
						</ul>
					) : (
						<p className="mt-2 text-xs text-muted-foreground">
							Nenhuma fila cadastrada.
						</p>
					)}
				</section>

				{/* ---------------- pedidos anteriores ---------------- */}
				{concluidos.length > 0 && (
					<section className="rounded-xl border bg-card p-3">
						<header className="flex items-center justify-between gap-2">
							<p className="text-sm font-medium">Pedidos anteriores</p>
							<KeyRound className="h-4 w-4 text-muted-foreground" />
						</header>

						<ul className="mt-2 space-y-1.5">
							{concluidos.map((pedido) => (
								<li key={pedido.id} className="text-xs">
									<p className="font-medium">
										{tituloDoPedido(
											pedido,
											nomeDaFerramentaDoPedido(pedido)
										)}
									</p>
									<p className="text-muted-foreground">
										{pedido.resultado ||
											(pedido.status === "recusado"
												? "recusado por você"
												: "concluído")}
									</p>
								</li>
							))}
						</ul>
					</section>
				)}
			</aside>
		</div>
	);
};

export default AbaAssistente;
